import Vue from 'vue';
import Vuex from 'vuex';
import i18n from './i18n';
import user from './user';
import clients from './clients';
import interaction from './interaction';
import report from './report';
import features from './features';
import selfService from './selfService';
import demo from './demo';
import certificate from './certificate';

import { fetchSystemConfig } from '@/services/authorization';
import { formatCurrency, formatNumber } from '@/utils/format';

Vue.use(Vuex);

const SHOW_MODAL = 'SHOW_MODAL';
const CLEAR_MODAL = 'CLEAR_MODAL';
const SHOW_TOAST = 'SHOW_TOAST';
const CLEAR_TOAST = 'CLEAR_TOAST';
const SET_BREADCRUMB = 'SET_BREADCRUMB';
const SET_BREADCRUMB_CONTEXT = 'SET_BREADCRUMB_CONTEXT';
const ADD_BREADCRUMB = 'ADD_BREADCRUMB';
const FETCH_SYSTEM_CONFIG_SUCCESS = 'FETCH_SYSTEM_CONFIG_SUCCESS';
const FETCH_SYSTEM_CONFIG_FAILURE = 'FETCH_SYSTEM_CONFIG_FAILURE';
export const START_LOADING = 'START_LOADING';
export const COMPLETE_LOADING = 'COMPLETE_LOADING';

export default new Vuex.Store({
  state: {
    app: {
      loading: 0,
      message: {
        type: null,
        message: '',
      },
      toast: null,
      breadcrumbItems: [],
      breadcrumbContext: null,
      modal: null,
    },
    systemConfig: null,
    isReady: false,
  },
  mutations: {
    START_LOADING(state) {
      state.app.loading = state.app.loading + 1;
    },
    COMPLETE_LOADING(state) {
      state.app.loading = state.app.loading - 1;
    },
    SHOW_MODAL(state, payload) {
      state.app.modal = payload;
    },
    CLEAR_MODAL(state) {
      state.app.modal = null;
    },
    SHOW_TOAST(state, payload) {
      state.app.toast = payload;
    },
    CLEAR_TOAST(state) {
      state.app.toast = null;
    },
    SET_BREADCRUMB(state, payload) {
      state.app.breadcrumbItems = payload;
    },
    SET_BREADCRUMB_CONTEXT(state, payload) {
      state.app.breadcrumbContext = {
        ...state.app.breadcrumbContext,
        ...payload,
      };
    },
    ADD_BREADCRUMB(state, payload) {
      state.app.breadcrumbItems = [...state.app.breadcrumbItems, payload];
    },
    FETCH_SYSTEM_CONFIG_SUCCESS(state, payload) {
      state.systemConfig = payload;
      state.isReady = true;
    },
    FETCH_SYSTEM_CONFIG_FAILURE(_, e) {
      console.error(e);
    },
  },
  getters: {
    appLoading: (state) => state.app.loading > 0,
    modal: (state) => state.app.modal,
    toast: (state) => state.app.toast,
    breadcrumbItems: (state) => state.app.breadcrumbItems,
    breadcrumbContext: (state) => state.app.breadcrumbContext,
    systemConfig: (state) => state.systemConfig,
    systemLocales: (state) => {
      const localesWithFormat = state.systemConfig?.locales
        // Filter out hidden locales
        .filter((locale) => !locale.hidden)
        // Extend with the formatting of the regional format, see utils/format
        .map((locale) => ({
          ...locale,
          dateFormatLong: locale.dateFormatLong.toUpperCase(),
          // currency: { code, symbol }, e.g. EN: "$118K", "€118K"; DE: "118 Tsd. $"
          currencyFormattedCompact(value, currency) {
            if (!currency?.code) return '';
            return formatCurrency(value, currency.code, locale, { compact: true });
          },
          numberFormatted(value) {
            return formatNumber(value, locale);
          },
        }));

      return localesWithFormat?.reduce((acc, locale) => {
        acc[locale.id] = locale;
        return acc;
      }, {});
    },
  },
  actions: {
    showModal({ commit }, payload) {
      commit(SHOW_MODAL, payload);
    },
    clearModal({ commit }) {
      commit(CLEAR_MODAL);
    },
    showToast({ commit }, payload) {
      commit(SHOW_TOAST, payload);
    },
    clearToast({ commit }) {
      commit(CLEAR_TOAST);
    },
    setBreadcrumb({ commit }, payload) {
      commit(SET_BREADCRUMB, payload);
    },
    addBreadcrumb({ commit }, payload) {
      commit(ADD_BREADCRUMB, payload);
    },
    setBreadcrumbContext({ commit }, payload) {
      commit(SET_BREADCRUMB_CONTEXT, payload);
    },
    async fetchSystemConfig({ commit }) {
      try {
        const config = await fetchSystemConfig();
        commit(FETCH_SYSTEM_CONFIG_SUCCESS, config);
      } catch (e) {
        commit(FETCH_SYSTEM_CONFIG_FAILURE, e);
      }
    },
  },
  modules: {
    i18n,
    user,
    clients,
    interaction,
    report,
    features,
    selfService,
    demo,
    certificate,
  },
});
